import { useState, useTransition } from "react";
import { toast } from "sonner";

import Pill from "@buildpy/ui/pill";
import StatusBadge from "@buildpy/ui/status-badge";
import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { Radio } from "@buildpy/ui/radio";
import { Textarea } from "@buildpy/ui/textarea";
import { settleRedirect } from "@/lib/redirect-error";
import { markCustomerContacted, markCustomerLost, revertCustomerStage } from "@/api/actions/customer";
import type { CustomerActionResult } from "@buildpy/core/modules/customer/action-types";
import type { HeadStage } from "@buildpy/core/modules/customer/journey-panel";
import { STAGE_REASON_MAX } from "@buildpy/core/modules/customer/stage-events";
import StageBadge from "@/modules/customer/ui/stage-badge";
import { CUSTOMER_STAGE_LABEL, CustomerStage } from "@buildpy/core/modules/customer/types";
import type { CodeOption } from "@buildpy/core/modules/org/codes";

interface Props {
  customerId: string;
  stage: CustomerStage;
  // 회사 코드값의 이탈 사유(사용 중인 것만). 서버가 만들어 넘긴다.
  lostReasons: CodeOption[];
  // 서버가 stageButtons 로 정한 값 — 누르면 막히지 않는 버튼만 보인다.
  buttons: { contact: boolean; lost: boolean; revert: boolean };
  // 되돌리면 갈 단계(마지막 이력의 이전 단계).
  revertTo: CustomerStage | null;
  // 이탈 고객이 견적을 다시 시작했으면 뱃지만 여정 단계로(headStageOf) — 되돌리기는 상세 page.tsx 가 headStage 가 있으면 revertTo 를 null 로 넘겨 끈다.
  display?: HeadStage | null;
}

// 단계는 사건(견적·계약)이 바꾼다 — 사람이 누르는 건 상담함·이탈 처리·되돌리기 셋뿐이다.
const StageMenu = ({ customerId, stage, lostReasons, buttons, revertTo, display }: Props) => {
  const [isPending, startTransition] = useTransition();
  const [isLostOpen, setIsLostOpen] = useState(false);
  const [isRevertOpen, setIsRevertOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [revertReason, setRevertReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const run = (call: () => Promise<CustomerActionResult>, success: string, done?: () => void) => {
    setError(null);
    startTransition(async () => {
      try {
        const result = await call();
        if (!result.ok) {
          setError(result.message);
          return;
        }
        toast.success(success);
        done?.();
      } catch (caught) {
        // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
        if (settleRedirect(caught)) return;
        setError("단계를 바꾸지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  // PageHeader 의 meta 는 <span> 안에 들어간다 — div 로 두면 DOM 중첩 경고가 난다.
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {display ? (
        <>
          <StatusBadge label={display.label} tone={display.tone} />
          <Pill>{display.note}</Pill>
        </>
      ) : (
        <StageBadge stage={stage} />
      )}

      {buttons.contact && (
        <Button disabled={isPending} size="sm" variant="outline" onClick={() => run(() => markCustomerContacted(customerId), "상담함으로 바꿨습니다")}>
          상담함
        </Button>
      )}
      {buttons.lost && (
        <Button disabled={isPending} size="sm" variant="outline" onClick={() => setIsLostOpen(true)}>
          이탈 처리
        </Button>
      )}
      {buttons.revert && revertTo && (
        <Button disabled={isPending} size="sm" variant="ghost" onClick={() => setIsRevertOpen(true)}>
          되돌리기
        </Button>
      )}

      {error && <span className="text-xs text-destructive">{error}</span>}

      <Dialog open={isLostOpen} onOpenChange={setIsLostOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>이탈 처리</DialogTitle>
            <DialogDescription>놓친 이유를 고릅니다. 나중에 이탈 사유를 모아 봅니다.</DialogDescription>
          </DialogHeader>

          {lostReasons.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              이탈 사유 코드값이 없습니다. 설정 → 코드값에서 먼저 추가하세요.
            </p>
          ) : (
            <div className="flex flex-col divide-y divide-border rounded-md border border-border">
              {lostReasons.map((reason) => (
                <label
                  key={reason.value}
                  className="flex cursor-pointer items-center gap-x-2 px-3 py-2 text-sm hover:bg-muted/50">
                  <Radio
                    aria-label={reason.label}
                    checked={lostReason === reason.value}
                    name="lost_reason"
                    onChange={() => setLostReason(reason.value)}
                    value={reason.value}
                  />
                  {reason.label}
                </label>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setIsLostOpen(false)}>
              취소
            </Button>
            <Button
              disabled={isPending || !lostReason.trim()}
              size="sm"
              onClick={() =>
                run(
                  () => markCustomerLost(customerId, lostReason.trim()),
                  "이탈로 바꿨습니다",
                  () => {
                    setIsLostOpen(false);
                    setLostReason("");
                  }
                )
              }>
              이탈로 바꾸기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRevertOpen} onOpenChange={setIsRevertOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>단계 되돌리기</DialogTitle>
            <DialogDescription>
              {revertTo
                ? `「${CUSTOMER_STAGE_LABEL[stage]}」 → 「${CUSTOMER_STAGE_LABEL[revertTo]}」 로 되돌립니다. 이유는 단계 이력에 남습니다.`
                : "되돌릴 단계 변경이 없습니다."}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            aria-label="되돌리는 사유"
            maxLength={STAGE_REASON_MAX}
            placeholder="예: 잘못 눌렀음"
            rows={3}
            value={revertReason}
            onChange={(event) => setRevertReason(event.target.value)}
          />

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setIsRevertOpen(false)}>
              취소
            </Button>
            <Button
              disabled={isPending || !revertReason.trim()}
              size="sm"
              onClick={() =>
                run(
                  () => revertCustomerStage(customerId, revertReason.trim()),
                  revertTo ? `「${CUSTOMER_STAGE_LABEL[revertTo]}」 단계로 되돌렸습니다` : "단계를 되돌렸습니다",
                  () => {
                    setIsRevertOpen(false);
                    setRevertReason("");
                  }
                )
              }>
              되돌리기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
};

export default StageMenu;
